/**
 * Helpers for the `/inventory` route's search params.
 *
 * The route's `validateSearch` types every key as required (the value may
 * be `undefined`, but the key must be present), so callers can't simply
 * spread `prev` when patching a single field. These helpers centralize the
 * full-key object so adding a new search param only touches one place.
 */

export interface InventorySearch {
  tab: string | undefined;
  // cp is a string in inventory context (counterparty group key) but the /trades
  // route also uses cp as a numeric counterparty id; the union here keeps
  // TanStack Router's global search-type merge compatible across both routes.
  cp: string | number | undefined;
  reportTab: string | undefined;
  view_id: number | undefined;
}

const emptyInventorySearch: InventorySearch = {
  tab: undefined,
  cp: undefined,
  reportTab: undefined,
  view_id: undefined,
};

/** Produce a full InventorySearch object, overriding only the given fields. */
export function mergeInventorySearch(
  prev: Partial<InventorySearch> | undefined,
  patch: Partial<InventorySearch>,
): InventorySearch {
  return { ...emptyInventorySearch, ...prev, ...patch };
}

export const isInventoryPath = (pathname: string) =>
  pathname === '/inventory' || pathname === '/inventory/';

/**
 * Typed accessor for the `cp` search param on the /inventory route.
 *
 * `InventorySearch.cp` is declared as `string | number | undefined` solely to
 * satisfy TanStack Router's global search-type merge with the /trades route
 * (which uses `cp: number`). At runtime on /inventory, validateSearch always
 * casts `cp` from the raw query string — so `cp` is always `string | undefined`.
 * The `number` variant is a dead branch on this route.
 *
 * Use this helper instead of accessing `search.cp` directly to prevent callers
 * from accidentally writing `number`-branch code that will never fire.
 */
export const getInventoryCp = (s: Pick<InventorySearch, 'cp'>): string | undefined =>
  typeof s.cp === 'string' ? s.cp : undefined;

/**
 * Resolve which tab the InventoryPage should display from the URL's `?tab`
 * value. URL is the source of truth — unknown / absent values fall back to
 * 'inventory' so a persisted tab from a prior session does not survive
 * logout or F5 (LR-447).
 */
export const resolveInventoryTab = (
  tab: string | undefined,
): 'inventory' | 'availability' | 'counterparty-reports' =>
  tab === 'availability' || tab === 'counterparty-reports' ? tab : 'inventory';

/**
 * `useRouterState` selectors that read the `/inventory` search params from the
 * router store. Shared by the view bootstrap and the tab sync so the
 * `(search)?.field` cast lives in one place. Return primitives so
 * `useRouterState`'s equality bailout keeps the value stable across re-renders.
 */
type RouterSearchState = { location: { search: unknown } };

export const selectInventoryTab = (s: RouterSearchState): string | undefined =>
  (s.location.search as Record<string, unknown>)?.tab as string | undefined;

export const selectInventoryViewId = (s: RouterSearchState): number | undefined => {
  // /inventory validateSearch coerces view_id to number | undefined, so the
  // string branch is dead here (cf. getInventoryCp for the same convention).
  // Require a TRUTHY number to mirror validateSearch's own `view_id ? … : undefined`:
  // a hand-edited `?view_id=0` / `?view_id=abc` yields 0 / NaN, which must fall
  // back to "no view" (the default-view branch), not be loaded as an id. NaN in
  // particular must not leak — it breaks useRouterState's `===` equality bailout.
  const raw = (s.location.search as Record<string, unknown>)?.view_id;
  return typeof raw === 'number' && raw ? raw : undefined;
};
